import { List } from 'antd';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { EditTicketFormValues } from '../../../../types/form';
import grispiApi from '../../../apis/grispiApi';
import useCurrentTicketChannel from '../../../hooks/ticketDetailPage/useCurrentTicketChannel';
import useCurrentTicketStatus from '../../../hooks/ticketDetailPage/useCurrentTicketStatus';
import {
    Comment,
    TICKET_FORM_FIELDS_KEY,
    TicketSystemFieldsKey,
    channelTypeMap,
    statusOptions
} from '../../../model/types';
import { GSocialStatus, useGSocialStatusDispatch } from '../../../store/provider/GSocialStatusProvider';
import { by, nonNullishValue } from '../../../utils/helpers';
import { isChannelGSocial } from '../CommentTypeSelect';
import Conversation from './Conversation';

interface ConversationListProps {
    comments: Comment[];
    showAvatar?: boolean;
    showAttachments?: boolean;
    isGSocialIntegrated?: boolean;
}

const COMMENT_POLLING_INTERVAL = 5000;
const LAST_COMMENT_CREATION_TIMEOUT = 60000;
const DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1000;

async function getComments(ticketKey: string, after: number) {
    const response = await grispiApi.get<Comment[]>(`tickets/${ticketKey}/comments?after=${after}`);
    return response.data;
}

const sortComments = <T extends Comment>(comments: T[]) =>
    (comments || []).sort(by<Comment>('createdAt')('descending'));

export default function ConversationList(props: ConversationListProps) {
    const { comments, isGSocialIntegrated = false, ...rest } = props;
    const {
        query: { ticketKey }
    } = useRouter();
    const ticketChannel = useCurrentTicketChannel();
    const ticketStatus = useCurrentTicketStatus();
    const changeGSocialStatus = useGSocialStatusDispatch();

    const [commentList, setCommentList] = useState<Comment[]>(sortComments(comments));
    const lastCommentCreatedAt = commentList?.[0]?.createdAt;
    const lastGSocialMessageCreatedAt = useMemo(
        () => commentList.find((comment) => isChannelGSocial(comment.channel))?.createdAt,
        [commentList]
    );

    const pollingInterval =
        Date.now() - lastGSocialMessageCreatedAt >= LAST_COMMENT_CREATION_TIMEOUT
            ? COMMENT_POLLING_INTERVAL * 2
            : COMMENT_POLLING_INTERVAL;

    const gSocialAccountId = useWatch<EditTicketFormValues, 'fields.ts$gSocial_account_id'>({
        name: `${TICKET_FORM_FIELDS_KEY}.${TicketSystemFieldsKey.gSocial.replace(new RegExp('\\.', 'gi'), '$')}` as any
    });
    useEffect(() => {
        setCommentList(sortComments(comments));
    }, [comments]);

    useEffect(() => {
        if (isGSocialIntegrated && nonNullishValue(lastGSocialMessageCreatedAt)) {
            const newGSocialStatus: GSocialStatus =
                ticketChannel === channelTypeMap.WHATSAPP
                    ? Date.now() - lastGSocialMessageCreatedAt <= DAY_IN_MILLISECONDS
                        ? 'active'
                        : 'inactive'
                    : 'active';
            changeGSocialStatus(newGSocialStatus);
        }
    }, [changeGSocialStatus, lastGSocialMessageCreatedAt, isGSocialIntegrated, ticketChannel]);

    useEffect(() => {
        const commentPolling =
            nonNullishValue(ticketStatus) &&
            nonNullishValue(lastCommentCreatedAt) &&
            ticketStatus !== statusOptions.Closed &&
            isGSocialIntegrated &&
            ticketKey &&
            setInterval(() => {
                getComments(ticketKey.toString(), lastCommentCreatedAt)
                    .then(
                        (commentList) =>
                            commentList && setCommentList((prevList) => [...sortComments(commentList), ...prevList])
                    )
                    .catch((error) => console.error(error));
            }, pollingInterval);
        return () => {
            clearInterval(commentPolling);
        };
    }, [isGSocialIntegrated, lastCommentCreatedAt, pollingInterval, ticketKey, ticketStatus]);

    const commentListLength = (commentList ?? []).length;

    return (
        <List
            dataSource={commentList}
            itemLayout="horizontal"
            renderItem={(comment, index) => (
                <Conversation
                    conversation={comment}
                    {...rest}
                    lastItem={commentListLength > 0 && index === commentListLength - 1}
                    to={gSocialAccountId?.toString()}
                />
            )}
        />
    );
}
